import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Search, PlusCircle, Trash2, Edit3, Eye, EyeOff } from "lucide-react";

const STATUS_COLORS = {
  available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  low_stock: "bg-amber-50 text-amber-700 border-amber-200",
  out_of_stock: "bg-rose-50 text-rose-700 border-rose-200",
  reserved: "bg-blue-50 text-blue-700 border-blue-200",
  sold: "bg-slate-100 text-slate-600 border-slate-200",
  hidden: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/admin/products", { params });
      setItems(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const toggleHidden = async (p) => {
    const newStatus = p.status === "hidden" ? "available" : "hidden";
    try {
      await api.patch(`/products/${p.id}`, { status: newStatus });
      toast.success(newStatus === "hidden" ? "Product hidden." : "Product visible.");
      load();
    } catch { toast.error("Update failed."); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Inventory</div>
          <h1 className="mt-1 font-display text-3xl font-bold">Manage products</h1>
        </div>
        <Link to="/admin/products/new" data-testid="inventory-add-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white font-medium text-sm hover:bg-blue-600">
          <PlusCircle className="w-4 h-4" /> Add product
        </Link>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-inventory-search"
            placeholder="Search by name, SKU, brand…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm"
          />
        </form>
        <select data-testid="admin-inventory-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-sm">
          <option value="">All statuses</option>
          <option value="available">Available</option>
          <option value="low_stock">Low stock</option>
          <option value="out_of_stock">Out of stock</option>
          <option value="reserved">Reserved</option>
          <option value="sold">Sold</option>
          <option value="hidden">Hidden</option>
        </select>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-inventory-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">SKU</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-right">Price</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="7" className="py-14 text-center text-slate-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="7" className="py-14 text-center text-slate-500">No products.</td></tr>
              ) : items.map(p => (
                <tr key={p.id} data-testid={`admin-row-${p.sku}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.brand}{p.model ? ` · ${p.model}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{p.sku}</td>
                  <td className="px-4 py-3 text-slate-700">{p.category}</td>
                  <td className="px-4 py-3 text-right font-semibold">${p.price?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{p.stock_quantity}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[p.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {p.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`row-toggle-hidden-${p.sku}`} onClick={() => toggleHidden(p)} title={p.status === "hidden" ? "Unhide" : "Hide"} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600">
                        {p.status === "hidden" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <Link data-testid={`row-edit-${p.sku}`} to={`/admin/products/${p.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600">
                        <Edit3 className="w-4 h-4" />
                      </Link>
                      <button data-testid={`row-delete-${p.sku}`} onClick={() => remove(p.id, p.name)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
